import { WSQueryCache } from "@ws-react-query";
import {
  IPayeeCreateRequest,
  IPayeeResponse
} from "@wingspanhq/payments/dist/interfaces";
import { QUERY_PAYEE } from "../../../query/payee/keys";
import {
  QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
  QUERY_PAYEE_ENGAGEMENTS_LIST,
  QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL,
  QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE
} from "../../../query/payeeEngagements/keys";
import { QUERY_PAYEE_ROWS } from "../../../query/search/payee/queries/useQueryPayeeRows";
import { QUERY_PAYEE_ROWS_SUMMARY } from "../../../query/search/payee/queries/useQueryPayeeRowsSummary";
import { createPayee } from "../../../services/payees";
import { createPayeeEngagementByPayeeId } from "../../../services/payeeEngagement/createPayeeEngagementByPayeeId";
import { BulkActionProgress, useForegroundBulkAction } from "../../BulkActions";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";

export interface CreateContractorBulkEngagement {
  id: string;
  name: string;
}

export interface CreateContractorBulkActionInput {
  payeeCreationData: Omit<IPayeeCreateRequest, "engagementId">;
  engagements: CreateContractorBulkEngagement[];
}

export const useCreateContractorBulkAction = (): ((
  input: CreateContractorBulkActionInput
) => Promise<IPayeeResponse | null>) => {
  const runBulkAction = useForegroundBulkAction();
  const xWingspanPayerId = useXWingspanPayerId();

  return async ({ payeeCreationData, engagements }) => {
    const total = 1 + engagements.length;
    let createdPayee: IPayeeResponse | null = null;

    await runBulkAction({
      actionName: "Add contractor and assignments",
      itemCount: total,
      skipConfirm: true,
      prepare: async () => ({ payeeCreationData, engagements }),
      process: async (data, onProgress): Promise<BulkActionProgress> => {
        let processed = 0;
        let successful = 0;
        let failed = 0;
        const successDescriptions: string[] = [];
        const failedDescriptions: string[] = [];

        onProgress({
          total,
          processed,
          successful,
          failed,
          progressMessage: "Creating contractor..."
        });

        try {
          const payee = await createPayee(data.payeeCreationData);
          WSQueryCache.setQueryData([QUERY_PAYEE, payee.payeeId, null], payee);
          createdPayee = payee;
          successful++;
          successDescriptions.push("Contractor created");
        } catch {
          failed++;
          processed++;
          failedDescriptions.push("Create contractor");
          onProgress({
            total,
            processed,
            successful,
            failed,
            progressMessage: "Failed to create contractor",
            failedDescriptions
          });
          return { total, processed, successful, failed, failedDescriptions };
        }

        processed++;
        onProgress({
          total,
          processed,
          successful,
          failed,
          progressMessage: "Contractor created",
          successDescriptions
        });

        for (const engagement of data.engagements) {
          onProgress({
            total,
            processed,
            successful,
            failed,
            progressMessage: `Assigning engagement ${processed} of ${data.engagements.length}...`,
            successDescriptions,
            failedDescriptions
          });
          try {
            await createPayeeEngagementByPayeeId(
              createdPayee!.payeeId,
              { engagementId: engagement.id, payerOwnedData: {} },
              xWingspanPayerId
            );
            successful++;
            successDescriptions.push(`Assigned "${engagement.name}"`);
          } catch {
            failed++;
            failedDescriptions.push(`Assign "${engagement.name}"`);
          }
          processed++;
          onProgress({
            total,
            processed,
            successful,
            failed,
            progressMessage: `Assigning engagement ${
              processed
            } of ${data.engagements.length}`,
            successDescriptions,
            failedDescriptions
          });
        }

        WSQueryCache.invalidateQueries(QUERY_PAYEE_ROWS);
        WSQueryCache.invalidateQueries(QUERY_PAYEE_ROWS_SUMMARY);
        WSQueryCache.invalidateQueries(QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID);
        WSQueryCache.invalidateQueries(QUERY_PAYEE_ENGAGEMENTS_LIST);
        WSQueryCache.invalidateQueries(
          QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL
        );
        WSQueryCache.invalidateQueries(QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE);
        WSQueryCache.invalidateQueries(QUERY_PAYEE);

        return {
          total,
          processed,
          successful,
          failed,
          successDescriptions,
          failedDescriptions
        };
      }
    });

    return createdPayee;
  };
};
